import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { listKeywords, listTopics, type SeoKeyword, type SeoTopic } from "../api/marketing.ts";

const INTENT_COLOR: Record<string, string> = {
  informational: "bg-blue-900/40 text-blue-300",
  commercial: "bg-orange-900/40 text-orange-300",
  transactional: "bg-green-900/40 text-green-300",
  navigational: "bg-gray-700/40 text-gray-400",
};

export function MarketingSeoPage() {
  const { token } = useAuth();
  const [keywords, setKeywords] = useState<SeoKeyword[]>([]);
  const [topics, setTopics] = useState<SeoTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"keywords" | "topics">("keywords");

  useEffect(() => {
    if (token == null) return;
    setLoading(true);
    Promise.all([
      listKeywords(token, { limit: 100 }),
      listTopics(token, { limit: 50 }),
    ])
      .then(([k, t]) => { setKeywords(k.items); setTopics(t.items); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-white">SEO</h1>

        {error != null && <p className="text-red-400 text-sm">{error}</p>}

        <div className="flex gap-2">
          {(["keywords", "topics"] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-1.5 text-sm rounded transition-colors ${tab === t ? "bg-indigo-600 text-white" : "bg-gray-800 text-gray-400 hover:text-white"}`}>
              {t === "keywords" ? `Keywords (${keywords.length})` : `Topics (${topics.length})`}
            </button>
          ))}
        </div>

        {loading && <p className="text-gray-400 text-sm">Loading…</p>}

        {tab === "keywords" && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b border-gray-800 text-gray-400">
                  <th className="pb-2 pr-4">Keyword</th>
                  <th className="pb-2 pr-4">Volume</th>
                  <th className="pb-2 pr-4">Difficulty</th>
                  <th className="pb-2 pr-4">CPC</th>
                  <th className="pb-2">Intent</th>
                </tr>
              </thead>
              <tbody>
                {keywords.map((k) => (
                  <tr key={k.id} className="border-b border-gray-800/50 hover:bg-gray-900/50">
                    <td className="py-2 pr-4 text-white">{k.keyword}</td>
                    <td className="py-2 pr-4 text-gray-400">{k.searchVolume?.toLocaleString() ?? "—"}</td>
                    <td className="py-2 pr-4 text-gray-400">{k.difficulty ?? "—"}</td>
                    <td className="py-2 pr-4 text-gray-400">{k.cpc != null ? `$${k.cpc.toFixed(2)}` : "—"}</td>
                    <td className="py-2">
                      {k.intent != null && (
                        <span className={`text-xs px-2 py-0.5 rounded ${INTENT_COLOR[k.intent] ?? "bg-gray-700 text-gray-400"}`}>{k.intent}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {keywords.length === 0 && !loading && (
                  <tr><td colSpan={5} className="py-4 text-center text-gray-500">No keywords yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "topics" && (
          <div className="space-y-3">
            {topics.map((t) => (
              <div key={t.id} className="rounded-lg border border-gray-800 p-4 space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-medium text-white">{t.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Pillar: <span className="text-gray-300">{t.pillarKeyword}</span></p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-400">{t.status}</span>
                </div>
                {t.supportingKeywords.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {t.supportingKeywords.slice(0, 8).map((kw) => (
                      <span key={kw} className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-400">{kw}</span>
                    ))}
                    {t.supportingKeywords.length > 8 && (
                      <span className="text-xs text-gray-500">+{t.supportingKeywords.length - 8} more</span>
                    )}
                  </div>
                )}
              </div>
            ))}
            {topics.length === 0 && !loading && (
              <p className="text-center text-gray-500 py-8">No topic clusters yet.</p>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
